import React, { useState } from 'react';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { Button } from 'reactstrap';
import axios from 'axios';
import { getPlanById } from 'config/subscriptionPlans';

// Separate axios instance for public endpoints (no auth header)
const publicAxios = axios.create();
publicAxios.interceptors.request.use((config) => {
  if (!config.baseURL) config.baseURL = axios.defaults.baseURL;
  return config;
});

const IndividualCodeRegistrationPayment = ({ code, email, name, priceAmount, planId, onBack, onComplete }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const plan = getPlanById(planId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!stripe || !elements) {
      setError('Payment system is still loading. Please wait.');
      return;
    }

    setLoading(true);

    try {
      // 1. Validate card input
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message);
        setLoading(false);
        return;
      }

      // 2. Create payment on backend (public endpoint)
      const { data } = await publicAxios.post('/createIndividualCodePaymentPublic', {
        code,
        email,
        name,
      });

      // 3. Confirm payment with Stripe
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret: data.clientSecret,
        confirmParams: {
          return_url: window.location.origin + '/auth/register',
        },
        redirect: 'if_required',
      });

      if (confirmError) {
        setError(confirmError.message);
        setLoading(false);
        return;
      }

      // 4. Pass results back to Register
      onComplete({
        customerId: data.customerId,
        paymentIntentId: paymentIntent.id,
        invoiceId: data.invoiceId,
        selectedPlanId: planId,
      });
    } catch (err) {
      console.error('Individual code registration payment error:', err);
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="text-center mb-4">
        <h6 className="text-muted mb-2" style={{ fontSize: 13 }}>Group Discount Price</h6>
        <div className="h2 text-success mb-1">${priceAmount}</div>
        {plan && (
          <small className="text-muted">
            {plan.name} — {plan.months} {plan.months === 1 ? 'Month' : 'Months'} of Access
          </small>
        )}
      </div>

      <div className="mb-3">
        <h6 className="text-muted mb-3" style={{ fontSize: 13 }}>
          Payment method
        </h6>
        <PaymentElement />
      </div>

      {error && (
        <div className="text-center mb-3">
          <small className="text-danger">{error}</small>
        </div>
      )}

      <div className="text-center d-flex justify-content-center" style={{ gap: '12px' }}>
        <Button className="mt-2" color="secondary" type="button" onClick={onBack} disabled={loading}>
          <i className="fas fa-arrow-left mr-1" /> Back
        </Button>
        <Button className="mt-2" color="success" type="button" onClick={handleSubmit} disabled={loading || !stripe}>
          {loading ? (
            <><i className="fas fa-spinner fa-spin mr-1" /> Processing...</>
          ) : (
            <>Pay ${priceAmount} & Create Account</>
          )}
        </Button>
      </div>
    </div>
  );
};

export default IndividualCodeRegistrationPayment;
